import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { login, handleOAuthSession } from "../utils/api";
import api from "../utils/api";
import { toast } from "sonner";
import { initiateGoogleLogin, getSessionIdFromUrl, clearSessionIdFromUrl } from "../utils/auth";
import OAuthLoadingModal from "@/components/OAuthLoadingModal";

const LoginPage = ({ setUser }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOAuthModal, setShowOAuthModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = getSessionIdFromUrl();
    if (sessionId) {
      handleGoogleAuth(sessionId);
    }
  }, []);

  const handleGoogleAuth = async (sessionId) => {
    console.log('[Google Auth] Processing session ID:', sessionId.substring(0, 10) + '...');
    setLoading(true);
    try {
      const response = await handleOAuthSession(sessionId);
      console.log('[Google Auth] OAuth API Success');
      console.log('[Google Auth] User:', response.data.user.email);
      console.log('[Google Auth] Profile completed:', response.data.user.profile_completed);
      console.log('[Google Auth] Session token in response:', response.data.session_token ? response.data.session_token.substring(0, 10) + '...' : 'Missing');
      
      // Check if cookies are enabled
      console.log('[Google Auth] Cookies enabled:', navigator.cookieEnabled);
      
      // Wait a moment for cookie to be set by browser
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Verify session was set by checking /auth/session
      try {
        const sessionCheck = await api.get('/auth/session');
        console.log('[Google Auth] Session verification SUCCESS:', sessionCheck.data.user.email);
      } catch (sessionError) {
        console.error('[Google Auth] Session verification FAILED:', sessionError.response?.status);
        console.error('[Google Auth] This means cookie was not set properly');
        toast.error("Authentication failed. Please try again.");
        clearSessionIdFromUrl();
        setLoading(false);
        return;
      }
      
      setUser(response.data.user);
      clearSessionIdFromUrl();
      toast.success("Logged in successfully with Google!");
      
      // Navigate based on profile completion
      if (!response.data.user.profile_completed) {
        console.log('[Google Auth] Navigating to complete-profile');
        navigate("/complete-profile");
      } else {
        console.log('[Google Auth] Navigating to member-dashboard');
        navigate("/member-dashboard");
      }
    } catch (error) {
      console.error('[Google Auth] Error:', error);
      console.error('[Google Auth] Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || "Google login failed. Please try again.";
      toast.error(errorMsg);
      clearSessionIdFromUrl();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await login({ email, password });
      setUser(response.data.user);
      toast.success("Logged in successfully!");
      navigate("/member-dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4 sm:px-6 py-6 sm:py-8">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <Button 
          onClick={() => navigate('/')}
          className="mb-4 sm:mb-6 border border-black bg-white text-black hover:bg-black hover:text-white transition-all text-sm sm:text-base min-h-[44px] px-4 sm:px-6"
        >
          ← Back
        </Button>
        
        <div className="text-center mb-6 sm:mb-8">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_haute-assets/artifacts/jb59wbkx_THC%20Final%20Logo%20%28Cutout%202%29.png" 
              alt="The Haute Circle" 
              className="h-32 sm:h-40 mx-auto mb-4 sm:mb-6" 
            />
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Welcome to The Haute Circle</h1>
          <p className="text-gray-600 text-xs sm:text-sm">Log-in/Sign-up to proceed</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full min-h-[44px]"
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full min-h-[44px]"
                data-testid="password-input"
              />
              <div className="text-right mt-2">
                <Link to="/forgot-password" className="text-xs sm:text-sm text-gold hover:underline">
                  Forgot Password?
                </Link>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-black text-white hover:bg-black/90 min-h-[48px] text-sm sm:text-base" 
              disabled={loading} 
              data-testid="login-submit-btn"
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>

          <div className="mt-4 sm:mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            {/* Google Sign In */}
            <Button
              onClick={() => {
                setShowOAuthModal(true);
                setTimeout(() => {
                  initiateGoogleLogin();
                }, 500);
              }}
              className="w-full mt-4 border border-gray-300 bg-white text-black hover:bg-gray-50 flex items-center justify-center min-h-[48px] text-sm sm:text-base"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2 flex-shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-sm sm:text-base">Continue with Google</span>
            </Button>
            
            {/* OAuth Loading Modal */}
            <OAuthLoadingModal 
              isOpen={showOAuthModal}
              onClose={() => setShowOAuthModal(false)}
              provider="Google"
            />
          </div>

          <p className="mt-4 sm:mt-6 text-center text-xs sm:text-sm">
            Don't have an account?{" "}
            <Link to="/signup" className="text-gold hover:underline font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;